import { type LangId, type Monetary, type ApiLangId } from '../types/general-data'
import { Locale, LocaleLanguage } from '@sim-api-enums/i18n'

export const LocaleToApiLang  = {
    [Locale.CS]: 'ces',
    [Locale.SK]: 'slk',
    [Locale.EN]: 'eng',
    [Locale.DE]: 'deu',
    [Locale.DE_AT]: 'bar',
} satisfies Record<Locale, ApiLangId>

export const LocaleLanguageToApiLang = {
    [LocaleLanguage.CS_CZ]: 'ces',
    [LocaleLanguage.SK_SK]: 'slk',
    [LocaleLanguage.EN_US]: 'eng',
    [LocaleLanguage.DE_DE]: 'deu',
    [LocaleLanguage.DE_AT]: 'bar',
} satisfies Record<LocaleLanguage, ApiLangId>

/**
 * Transforms the locale code from the current i18n locale (ISO 639-1; e.g. `en` or `en-US`)
 * to the language code used in the API (ISO 639-2/B; e.g. `eng`).
 *
 * @returns The language code used in the API or `null` if the language code is not supported.
 */
export function mapLocaleToApiLanguageId(code: Locale | null | undefined) {
    if (!code) return null
    return LocaleToApiLang[code] ?? null
}

/**
 * Transforms the locale language code for the current i18n locale (e.g. `en-US`)
 * to the language code used in the API (ISO 639-2/B; e.g. `eng`).
 *
 * @returns The language code used in the API or `null` if the language code is not supported.
 */
export function mapLocaleLanguageToApiLanguageId(code: LocaleLanguage | null | undefined) {
    if (!code) return null
    return LocaleLanguageToApiLang[code] ?? null
}

export function mapLocaleToIso1Code(locale: Locale | null | undefined) {
    if (!locale) return undefined
    const parts = locale.split('-')
    return parts[0] as LangId
}

/**
 * Get the formatted price from the monetary object.
 * Can return `null` if the monetary object is not suitable. (e.g. not an object but a number and so on)
 *
 * Only this function should be used to get the formatted price.
 * @param monetary
 */
export function getFormattedPrice(monetary: Monetary | null | undefined): string | null {
    if (monetary === null || monetary === undefined) return null
    // if monetary is a number, there is no formatted price
    if (typeof monetary === 'number') return null

    // if we're sending the currently selected currency, monetary only represents the current currency
    if ('formatted' in monetary && typeof monetary.formatted === 'string') return monetary.formatted

    // monetary is an object with every currency, return the currently selected currency
    // TODO
    return null
}

/**
 * Get the rounded price from the monetary object.
 * Can return `null` if the monetary object is not suitable. (e.g. not an object but a number and so on)
 *
 * Only this function should be used to get the rounded price.
 * @param monetary
 */
export function getRoundedPrice(monetary: Monetary | null | undefined): number | null {
    if (monetary === null || monetary === undefined) return null
    // if monetary is a number, there is no rounded price
    if (typeof monetary === 'number') return null

    // if we're sending the currently selected currency, monetary only represents the current currency
    if ('rounded' in monetary && typeof monetary.rounded === 'number') return monetary.rounded

    // monetary is an object with every currency, return the currently selected currency
    // TODO
    return null
}

/**
 * Get the numeric value from the monetary object.
 * Only this function should be used to get the price value.
 * @param monetary
 */
export function getPriceValue(monetary: Monetary): number
export function getPriceValue(monetary: Monetary | null | undefined): number | null
export function getPriceValue(monetary: Monetary | null | undefined): number | null {
    if (monetary === null || monetary === undefined) return null

    // if monetary is a number, return it
    if (typeof monetary === 'number') return monetary

    // if we're sending the currently selected currency, monetary only represents the current currency
    if ('value' in monetary && typeof monetary.value === 'number') return monetary.value

    // monetary is an object with every currency, return the currently selected currency
    // TODO
    return null
}
